import cuid from "cuid";
import dayjs from "dayjs";
import { useFormik } from "formik";
import { get } from "lodash";
import moment from "moment";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMediaQuery } from "react-responsive";
import { NAME_SPACES } from "shared/locales/constants";
import { TERMINATION_RECIPIENTS } from "utils/api/graphql/queries/termination";
import {
  DATE_FORMAT,
  FAMILY_SITUATION,
  FRANCE_DEPARTMENTS,
  JOB_LIST,
  SOCIO_PROFESSIONAL_CATEGORIES,
} from "utils/constants";
import { getMaxDeltaEnd, getMinDeltaStart } from "utils/helpers/array";
import { bindDateInputProps, bindInputProps } from "utils/helpers/input";
import { filterContractNumber } from "utils/helpers/validations";
import { ReactComponent as InfoIcon } from "../Assets/icons/info.svg";
import ActionButtons from "../Components/ActionButtons";
import CheckboxPanel from "../Components/CheckboxPanel";
import DataProtection from "../Components/DataProtection";
import Header from "../Components/Header";
import Input from "../Components/Input";
import Layout from "../Components/Layout";
import Select from "../Components/Select";
import Toggle from "../Components/Toggle";
import UpdateModal from "./Widgets/UpdateModal";
import validation from "./validation";

function View({
  contract,
  ria,
  onSubmit,
  onBack,
  deltas,
  locked,
  fields,
  terminationRequest,
  isCollective,
  translate,
  recalculateCost,
  subscription,
  project,
}) {
  const { t } = useTranslation();
  const { t: tProcesses } = useTranslation(NAME_SPACES.PUBLIC.PROCESSES);
  const COUNTRIES = tProcesses("DETAIL.COUNTRIES", { returnObjects: true });
  const [showChangeCostModal, setShowChangeCostModal] = useState({});
  const [oldIssuanceDate, setOldIssuanceDate] = useState(
    get(contract, "issuanceDate"),
  );
  const [validStartDate, setValidStartDate] = useState();
  const [validEndDate, setValidEndDate] = useState();
  const [pickerValue, setPickerValue] = useState(
    !get(contract, "issuanceDate") && dayjs(),
  );
  const isMobile = useMediaQuery({ maxWidth: 768 });

  // Build countries options from translations
  const COUNTRY_OPTIONS = useMemo(
    () =>
      Array.isArray(COUNTRIES)
        ? COUNTRIES.map((country) => ({
            value: country,
            label: country,
          }))
        : [],
    [COUNTRIES],
  );

  // Build department options from FRANCE_DEPARTMENTS constant
  const DEPARTMENT_OPTIONS = useMemo(
    () =>
      FRANCE_DEPARTMENTS.map(({ code, name }) => ({
        value: name,
        label: code ? `(${code}) ${name}` : name,
      })),
    [],
  );

  // Build family status options from translations
  const FAMILY_STATUS_OPTIONS = useMemo(
    () => [
      {
        value: FAMILY_SITUATION.SINGLE,
        label: translate?.FAMILY_STATUS_OPTIONS?.SINGLE || "Célibataire",
      },
      {
        value: FAMILY_SITUATION.MARRIED,
        label: translate?.FAMILY_STATUS_OPTIONS?.MARRIED || "Marié(e)",
      },
      {
        value: FAMILY_SITUATION.PACS,
        label: translate?.FAMILY_STATUS_OPTIONS?.PACS || "Pacsé(e)",
      },
      {
        value: FAMILY_SITUATION.DIVORCED,
        label: translate?.FAMILY_STATUS_OPTIONS?.DIVORCED || "Divorcé(e)",
      },
      {
        value: FAMILY_SITUATION.WIDOWED,
        label: translate?.FAMILY_STATUS_OPTIONS?.WIDOWED || "Veuf(ve)",
      },
      {
        value: FAMILY_SITUATION.COHABITING,
        label: translate?.FAMILY_STATUS_OPTIONS?.COHABITING || "Concubinage",
      },
    ],
    [translate],
  );

  // Build profession options from translations using SOCIO_PROFESSIONAL_CATEGORIES
  const PROFESSION_OPTIONS = useMemo(
    () => [
      {
        value: SOCIO_PROFESSIONAL_CATEGORIES.FARMERS,
        label: translate?.PROFESSION_OPTIONS?.FARMERS,
      },
      {
        value: SOCIO_PROFESSIONAL_CATEGORIES.ARTISANS,
        label: translate?.PROFESSION_OPTIONS?.ARTISANS,
      },
      {
        value: SOCIO_PROFESSIONAL_CATEGORIES.TRADERS_AND_SIMILAR,
        label: translate?.PROFESSION_OPTIONS?.TRADERS_AND_SIMILAR,
      },
      {
        value: SOCIO_PROFESSIONAL_CATEGORIES.ENTREPRENEURS,
        label: translate?.PROFESSION_OPTIONS?.ENTREPRENEURS,
      },
      {
        value: SOCIO_PROFESSIONAL_CATEGORIES.LIBERAL_PROFESSIONS_AND_SIMILAR,
        label: translate?.PROFESSION_OPTIONS?.LIBERAL_PROFESSIONS_AND_SIMILAR,
      },
      {
        value:
          SOCIO_PROFESSIONAL_CATEGORIES.EXECUTIVES_AND_PUBLIC_SERVICE_EMPLOYEES,
        label:
          translate?.PROFESSION_OPTIONS
            ?.EXECUTIVES_AND_PUBLIC_SERVICE_EMPLOYEES,
      },
      {
        value: SOCIO_PROFESSIONAL_CATEGORIES.EXECUTIVES,
        label: translate?.PROFESSION_OPTIONS?.EXECUTIVES,
      },
      {
        value: SOCIO_PROFESSIONAL_CATEGORIES.EMPLOYEES_AND_SUPERVISORS,
        label: translate?.PROFESSION_OPTIONS?.EMPLOYEES_AND_SUPERVISORS,
      },
      {
        value: SOCIO_PROFESSIONAL_CATEGORIES.WORKERS,
        label: translate?.PROFESSION_OPTIONS?.WORKERS,
      },
      {
        value: SOCIO_PROFESSIONAL_CATEGORIES.RETIREES,
        label: translate?.PROFESSION_OPTIONS?.RETIREES,
      },
      {
        value:
          SOCIO_PROFESSIONAL_CATEGORIES.PEOPLE_WITHOUT_PROFESSIONAL_ACTIVITY,
        label:
          translate?.PROFESSION_OPTIONS?.PEOPLE_WITHOUT_PROFESSIONAL_ACTIVITY,
      },
    ],
    [translate],
  );

  // Build professional situation options
  const PROFESSIONAL_SITUATION_OPTIONS = useMemo(
    () => [
      {
        value: JOB_LIST.ACTIVE,
        label: translate?.FORM?.PROFESSIONAL_SITUATION?.ACTIVE || "En activité",
      },
      {
        value: JOB_LIST.RETIRED,
        label: translate?.FORM?.PROFESSIONAL_SITUATION?.RETIRED || "Retraité",
      },
    ],
    [translate],
  );

  const { handleSubmit, values, setFieldValue, ...formProps } = useFormik({
    initialValues: {
      contract,
      ria: ria || false,
      locked,
      fields: {
        ...fields,
        hasExistingContract: fields?.hasExistingContract || false,
        contractMoreThanOneYear: fields?.contractMoreThanOneYear || false,
        selfOccupation: fields?.selfOccupation,
        isPPE: fields?.isPPE || false,
        ppeFunction: fields?.ppeFunction || "",
        ppeRelated: fields?.ppeRelated || false,
        familyStatus: fields?.familyStatus || null,
        birthCountry: fields?.birthCountry || "France",
        birthDepartment: fields?.birthDepartment || null,
        profession:
          fields?.profession ||
          SOCIO_PROFESSIONAL_CATEGORIES.EXECUTIVES_AND_PUBLIC_SERVICE_EMPLOYEES,
        professionalSituation: fields?.professionalSituation || null,
      },
      terminationRequest,
    },
    onSubmit: onSubmit,
    validateOnMount: true,
    validationSchema: validation(
      t(`${NAME_SPACES.ERRORS}:VALIDATIONS`, { returnObjects: true }),
      {
        deltaRia: get(deltas, "start.ria"),
        deltaStart: get(deltas, "start.normal"),
        deltaEndRia: get(deltas, "end.ria"),
        deltaEnd: get(deltas, "end.normal"),
      },
    ),
  });

  // CA7 - Check if RIA fields are required
  const isRiaRequired = useMemo(() => {
    const hasExistingContract = values?.fields?.hasExistingContract;
    const contractMoreThanOneYear = values?.fields?.contractMoreThanOneYear;
    const selfOccupation = values?.fields?.selfOccupation;
    return (
      hasExistingContract && contractMoreThanOneYear && selfOccupation === false
    );
  }, [values]);

  // CA7 - Error message for required fields
  const REQUIRED_ERROR = t(`${NAME_SPACES.ERRORS}:VALIDATIONS.REQUIRED`);

  // CA7 - Check individual RIA field errors
  const riaFieldErrors = useMemo(() => {
    if (!isRiaRequired) return {};

    const errors = {};
    if (!values?.terminationRequest?.dueDate) {
      errors.dueDate = REQUIRED_ERROR;
    }
    if (!values?.terminationRequest?.recipient) {
      errors.recipient = REQUIRED_ERROR;
    }
    return errors;
  }, [isRiaRequired, values, REQUIRED_ERROR]);

  // CA7 - Check if RIA fields are valid when Miltis handles termination
  const isRiaFieldsValid = useMemo(() => {
    return Object.keys(riaFieldErrors).length === 0;
  }, [riaFieldErrors]);

  // Form validation combines Yup schema validation and manual RIA validation
  const isValid = formProps.isValid && isRiaFieldsValid;

  useEffect(() => {
    const insurancePlans = [get(subscription, "insurancePlan")];
    const deltaRia = getMinDeltaStart({ insurancePlans, minValue: 34 });
    const deltaStart = getMinDeltaStart({
      insurancePlans,
      minValue: 1,
      key: "DELTA_START",
    });
    const deltaEndRia = getMaxDeltaEnd({ insurancePlans }) || 0;
    const deltaEnd =
      getMaxDeltaEnd({
        insurancePlans,
        key: "DELTA_END",
      }) || 0;
    const validEndDate =
      values?.ria && values?.fields?.selfOccupation === false
        ? deltaEndRia === 0
          ? dayjs().endOf("year")
          : dayjs().add(deltaEndRia, "days")
        : deltaEnd === 0
          ? dayjs().endOf("year")
          : dayjs().add(deltaEnd, "days");
    const validStartDate =
      values?.ria && values?.fields?.selfOccupation === false
        ? dayjs(
            moment
              .max(
                moment().add(deltaRia, "days").startOf("day"),
                moment().startOf("y"),
              )
              .toDate(),
          )
        : dayjs(
            moment
              .max(
                moment().add(deltaStart, "days").startOf("day"),
                moment().startOf("y"),
              )
              .toDate(),
          );
    setValidStartDate(validStartDate);
    setValidEndDate(validEndDate);
  }, [values?.ria, values?.fields?.selfOccupation, subscription, deltas]);

  const checkExpired = useCallback(
    ({ issuanceDate }) => {
      const deltaRia = get(deltas, "start.ria", 34);
      const minIssuanceDate = dayjs().startOf("day").add(deltaRia, "days");
      const expired = dayjs(issuanceDate).isBefore(minIssuanceDate);
      return expired;
    },
    [deltas],
  );

  // Handle existing contract checkbox change
  const handleExistingContractChange = (checked) => {
    setFieldValue("fields.hasExistingContract", checked);
    setFieldValue("ria", checked);
    if (!checked) {
      setFieldValue("fields.contractMoreThanOneYear", false);
      setFieldValue("fields.selfOccupation", undefined);
      setFieldValue("terminationRequest", null);
    }
  };

  // Handle contract more than one year toggle
  const handleContractMoreThanOneYearChange = (checked) => {
    setFieldValue("fields.contractMoreThanOneYear", checked);
    if (checked) {
      // Default to self-handling when toggle is turned on
      setFieldValue("fields.selfOccupation", true);
      setFieldValue("terminationRequest", null);
    } else {
      setFieldValue("fields.selfOccupation", undefined);
      setFieldValue("terminationRequest", null);
    }
  };

  // Handle termination method change
  const handleTerminationMethodChange = (isMiltisHandling) => {
    setFieldValue("fields.selfOccupation", !isMiltisHandling);
    if (isMiltisHandling) {
      setFieldValue("terminationRequest", {
        id: cuid(),
        reason: "22",
        brokerageMandate: { id: cuid() },
      });
      // Update effective date for RIA
      const newIssuanceDate = dayjs().add(deltas.start.ria, "days");
      setFieldValue("contract.issuanceDate", newIssuanceDate);
      setPickerValue(newIssuanceDate);
      if (recalculateCost) {
        recalculateCost({
          oldIssuanceDate: oldIssuanceDate,
          issuanceDate: newIssuanceDate,
          setFieldValue,
          setShowChangeCostModal,
          isRia: true,
        });
      }
    } else {
      setFieldValue("terminationRequest", null);
    }
  };

  const onChangeCost = () => {
    setShowChangeCostModal({});
  };

  const onCancelChangeCost = () => {
    // Close modal immediately
    setShowChangeCostModal({});

    // Revert changes if needed
    if (showChangeCostModal?.isRia) {
      setFieldValue("ria", false);
      setFieldValue("fields.selfOccupation", undefined);
      setFieldValue("contract.issuanceDate", oldIssuanceDate);
      setValidStartDate(dayjs().add(deltas.start.normal, "days"));
      setPickerValue(dayjs().add(deltas.start.normal, "days"));
    }

    // Revert the issuance date and recalculate cost
    if (recalculateCost && oldIssuanceDate) {
      setFieldValue("contract.issuanceDate", oldIssuanceDate);
      recalculateCost({
        oldIssuanceDate: values?.contract?.issuanceDate,
        issuanceDate: oldIssuanceDate,
        setFieldValue,
        setShowChangeCostModal,
        isCancel: true,
      });
    }
  };

  // Handle PPE checkbox change
  const handlePPEChange = (checked) => {
    setFieldValue("fields.isPPE", checked);
    if (!checked) {
      setFieldValue("fields.ppeFunction", "");
      setFieldValue("fields.ppeRelated", false);
    }
  };

  // Render the existing contract expanded content
  const renderExistingContractContent = () => {
    const contractMoreThanOneYear = values?.fields?.contractMoreThanOneYear;
    const selfOccupation = values?.fields?.selfOccupation;

    return (
      <div className="miltis__checkbox-panel--expand">
        {/* Toggle for contract duration */}
        <div className="miltis__checkbox-panel--row">
          <span
            className="miltis__checkbox-panel--question"
            dangerouslySetInnerHTML={{
              __html:
                translate?.EXISTING_CONTRACT?.QUESTION_MORE_THAN_YEAR ||
                "Possédez-vous ce contrat depuis <strong>plus d'un an</strong> ?",
            }}
          />
          <Toggle
            checked={contractMoreThanOneYear}
            onChange={handleContractMoreThanOneYearChange}
          />
        </div>

        {/* Show info message when toggle is OFF */}
        {!contractMoreThanOneYear && (
          <div className="miltis__info-message">
            <InfoIcon />
            <span
              dangerouslySetInnerHTML={{
                __html:
                  translate?.EXISTING_CONTRACT?.INFO_MESSAGE_LESS_THAN_YEAR ||
                  "Votre contrat ne pourra prendre effet au minimum que <strong>30 jours après votre souscription</strong>, ce qui pourrait entraîner une modification de la date d'effet initialement prévue.",
              }}
            />
          </div>
        )}

        {/* Show termination options when toggle is ON */}
        {contractMoreThanOneYear && (
          <>
            <div className="miltis__checkbox-panel--divider" />
            <div className="miltis__checkbox-panel--subtitle">
              {translate?.EXISTING_CONTRACT?.TERMINATION_QUESTION ||
                "Comment souhaitez-vous gérer la résiliation de votre ancien contrat ?"}
            </div>

            {/* Option 1: Miltis handles termination */}
            <div className="miltis__checkbox-panel--option">
              <button
                type="button"
                className={`miltis__radio-btn ${
                  selfOccupation === false ? "miltis__radio-btn--checked" : ""
                }`}
                onClick={() => handleTerminationMethodChange(true)}
              >
                <div className="miltis__radio-btn--circle" />
              </button>
              <span
                className="miltis__checkbox-panel--option-label"
                dangerouslySetInnerHTML={{
                  __html:
                    translate?.EXISTING_CONTRACT?.OPTION_MILTIS ||
                    "Je souhaite que <strong>Miltis effectue pour mon compte, la résiliation</strong> de mon contrat actuellement en vigueur auprès de mon assureur.",
                }}
              />
            </div>

            {/* Contract info fields - shown when Miltis handles termination (CA1, CA5) */}
            {selfOccupation === false && (
              <div className="miltis__checkbox-panel--fields">
                {/* N° de référence de votre contrat - NOT required (CA1) */}
                <Input
                  label={
                    translate?.EXISTING_CONTRACT?.CONTRACT_NUMBER?.LABEL ||
                    "N° de référence de votre contrat"
                  }
                  placeholder={
                    translate?.EXISTING_CONTRACT?.CONTRACT_NUMBER
                      ?.PLACEHOLDER || "ex : 123456789"
                  }
                  {...bindInputProps({
                    name: "terminationRequest.contractNumber",
                    values,
                    setFieldValue,
                    ...formProps,
                  })}
                  onChange={({ target: { value } }) => {
                    const filteredValue = filterContractNumber(value);
                    setFieldValue(
                      "terminationRequest.contractNumber",
                      filteredValue,
                    );
                  }}
                  onBlur={({ target: { value } }) => {
                    const trimmedValue = value.trim();
                    setFieldValue(
                      "terminationRequest.contractNumber",
                      trimmedValue,
                    );
                    formProps.setFieldTouched(
                      "terminationRequest.contractNumber",
                      true,
                    );
                  }}
                  required
                  // disabled={locked}
                />
                {/* Date d'effet de votre contrat actuel - REQUIRED (CA2) */}
                <Input
                  label={
                    translate?.EXISTING_CONTRACT?.CURRENT_EFFECTIVE_DATE
                      ?.LABEL || "Date d'effet de votre contrat actuel"
                  }
                  placeholder={
                    translate?.EXISTING_CONTRACT?.CURRENT_EFFECTIVE_DATE
                      ?.PLACEHOLDER || "JJ/MM/AAAA"
                  }
                  type="date"
                  variant="simple"
                  format={DATE_FORMAT}
                  defaultPickerValue={dayjs().subtract(1, "year")}
                  disabledDate={(current) => {
                    return current && current > dayjs().subtract(1, "year");
                  }}
                  {...bindDateInputProps({
                    name: "terminationRequest.dueDate",
                    values,
                    setFieldValue,
                    ...formProps,
                  })}
                  errors={
                    formProps.touched?.terminationRequest?.dueDate
                      ? riaFieldErrors.dueDate
                      : undefined
                  }
                  touched={formProps.touched?.terminationRequest?.dueDate}
                  required
                  // disabled={locked}
                />
                {/* Nom de votre assureur actuel - REQUIRED (CA3) */}
                <Select
                  type="async"
                  node={{
                    query: TERMINATION_RECIPIENTS,
                    variables: {},
                    accessors: {
                      root: "terminationRecipients.data",
                      label: "name",
                      value: "id",
                    },
                  }}
                  label={
                    translate?.EXISTING_CONTRACT?.CURRENT_COMPANY?.LABEL ||
                    "Nom de votre assureur actuel"
                  }
                  placeholder={
                    translate?.EXISTING_CONTRACT?.CURRENT_COMPANY
                      ?.PLACEHOLDER || "Sélectionnez"
                  }
                  value={
                    get(values, "terminationRequest.recipient")
                      ? Number(get(values, "terminationRequest.recipient"))
                      : undefined
                  }
                  onChange={(value) => {
                    setFieldValue(
                      "terminationRequest.recipient",
                      value ? String(value) : "",
                    );
                    formProps.setFieldTouched(
                      "terminationRequest.recipient",
                      true,
                    );
                  }}
                  errors={
                    formProps.touched?.terminationRequest?.recipient
                      ? riaFieldErrors.recipient
                      : undefined
                  }
                  touched={formProps.touched?.terminationRequest?.recipient}
                  required
                  // disabled={locked}
                />
              </div>
            )}

            {/* Option 2: Self-handling */}
            <div className="miltis__checkbox-panel--option">
              <button
                type="button"
                className={`miltis__radio-btn ${
                  selfOccupation === true ? "miltis__radio-btn--checked" : ""
                }`}
                onClick={() => handleTerminationMethodChange(false)}
              >
                <div className="miltis__radio-btn--circle" />
              </button>
              <span
                className="miltis__checkbox-panel--option-label"
                dangerouslySetInnerHTML={{
                  __html:
                    translate?.EXISTING_CONTRACT?.OPTION_SELF ||
                    "Je me charge de <strong>résilier moi-même.</strong>",
                }}
              />
            </div>

            {/* Info message - shown only when self-handling is selected (CA6) */}
            {selfOccupation === true && (
              <div className="miltis__info-message">
                <InfoIcon />
                <span
                  dangerouslySetInnerHTML={{
                    __html:
                      translate?.EXISTING_CONTRACT
                        ?.INFO_MESSAGE_LESS_THAN_YEAR ||
                      "Votre contrat ne pourra prendre effet au minimum que <strong>30 jours après votre souscription</strong>, ce qui pourrait entraîner une modification de la date d'effet initialement prévue.",
                  }}
                />
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  // Render the PPE expanded content
  const renderPPEContent = () => {
    const ppeRelated = values?.fields?.ppeRelated;

    return (
      <div className="miltis__checkbox-panel--expand">
        {/* PPE Function dropdown */}
        <div className="miltis__checkbox-panel--row">
          <span
            className="miltis__checkbox-panel--question"
            dangerouslySetInnerHTML={{
              __html:
                translate?.PPE?.FUNCTION_QUESTION ||
                "Quelles <strong>sont/étaient vos fonctions</strong> ?",
            }}
          />
          <Input
            placeholder={
              translate?.PPE?.FUNCTION_PLACEHOLDER || "Saisi votre fonction"
            }
            {...bindInputProps({
              name: "fields.ppeFunction",
              values,
              ...formProps,
            })}
            onChange={({ target: { value } }) =>
              setFieldValue("fields.ppeFunction", value)
            }
          />
        </div>

        <div className="miltis__checkbox-panel--divider" />

        {/* PPE Related toggle */}
        <div className="miltis__checkbox-panel--row">
          <span
            className="miltis__checkbox-panel--question"
            dangerouslySetInnerHTML={{
              __html:
                translate?.PPE?.RELATED_QUESTION ||
                "Avez-vous un lien familial ou êtes-vous étroitement <strong>associé avec une Personne Politiquement Exposée</strong> (PPE*) ?",
            }}
          />
          <Toggle
            checked={ppeRelated}
            onChange={(checked) => setFieldValue("fields.ppeRelated", checked)}
          />
        </div>
      </div>
    );
  };

  return (
    <Layout>
      <UpdateModal
        showChangeCostModal={showChangeCostModal}
        insurancePlanName={get(subscription, "insurancePlan.name")}
        setShowChangeCostModal={setShowChangeCostModal}
        onChangeCost={onChangeCost}
        onCancelChangeCost={onCancelChangeCost}
      />
      <Header
        title={
          translate?.HEADER?.TITLE || "Quelques informations complémentaires …"
        }
        highlight={translate?.HEADER?.HIGHLIGHT || "complémentaires …"}
        subtitle={translate?.HEADER?.SUBTITLE || "Pour mieux vous connaitre"}
      />
      <div className="miltis-form">
        <div className="miltis-form__container">
          {/* Situation familiale */}
          <div className="miltis-form__row">
            <Select
              label={
                translate?.FORM?.FAMILY_STATUS?.LABEL || "Situation familiale"
              }
              placeholder={
                translate?.FORM?.FAMILY_STATUS?.PLACEHOLDER || "Sélectionnez"
              }
              options={FAMILY_STATUS_OPTIONS}
              value={get(values, "fields.familyStatus") || undefined}
              {...bindInputProps({
                name: "fields.familyStatus",
                values,
                ...formProps,
              })}
              // disabled={locked}
            />
            {/* Pays de naissance */}
            <Select
              label={
                translate?.FORM?.BIRTH_COUNTRY?.LABEL || "Pays de naissance"
              }
              placeholder={
                translate?.FORM?.BIRTH_COUNTRY?.PLACEHOLDER || "Sélectionnez"
              }
              options={COUNTRY_OPTIONS}
              value={get(values, "fields.birthCountry") || undefined}
              showSearch
              {...bindInputProps({
                name: "fields.birthCountry",
                values,
                ...formProps,
              })}
              onChange={(value) => {
                setFieldValue("fields.birthCountry", value);
                // Clear department if country is not France
                if (value !== "France") {
                  setFieldValue("fields.birthDepartment", "Autre/Etranger");
                } else {
                  setFieldValue("fields.birthDepartment", null);
                }
              }}
              // disabled={locked}
            />
            {/* Département de naissance - Only shown when country is France */}
            <Select
              label={
                translate?.FORM?.BIRTH_DEPARTMENT?.LABEL ||
                "Département de naissance"
              }
              placeholder={
                translate?.FORM?.BIRTH_DEPARTMENT?.PLACEHOLDER || "Sélectionnez"
              }
              options={DEPARTMENT_OPTIONS}
              value={get(values, "fields.birthDepartment") || undefined}
              showSearch
              {...bindInputProps({
                name: "fields.birthDepartment",
                values,
                ...formProps,
              })}
              disabled={get(values, "fields.birthCountry") !== "France"}
            />
            {/* Profession */}
            <Select
              label={translate?.FORM?.PROFESSION?.LABEL || "Profession"}
              placeholder={
                translate?.FORM?.PROFESSION?.PLACEHOLDER || "Sélectionnez"
              }
              options={PROFESSION_OPTIONS}
              value={get(values, "fields.profession") || undefined}
              {...bindInputProps({
                name: "fields.profession",
                values,
                setFieldValue,
                ...formProps,
              })}
              disabled={true}
            />
            {/* Situation professionnelle */}
            <Select
              label={
                translate?.FORM?.PROFESSIONAL_SITUATION?.LABEL ||
                "Situation professionnelle"
              }
              placeholder={
                translate?.FORM?.PROFESSIONAL_SITUATION?.PLACEHOLDER ||
                "Sélectionnez"
              }
              options={PROFESSIONAL_SITUATION_OPTIONS}
              value={get(values, "fields.professionalSituation") || undefined}
              {...bindInputProps({
                name: "fields.professionalSituation",
                values,
                setFieldValue,
                ...formProps,
              })}
              required
              // disabled={locked}
            />
            {/* Date d'effet */}
            <Input
              label={translate?.FORM?.EFFECTIVE_DATE?.LABEL || "Date d'effet"}
              placeholder={
                translate?.FORM?.EFFECTIVE_DATE?.PLACEHOLDER || "JJ/MM/AAAA"
              }
              onFocus={() =>
                setOldIssuanceDate(get(values, "contract.issuanceDate"))
              }
              type="date"
              variant="simple"
              format={DATE_FORMAT}
              defaultPickerValue={pickerValue}
              disabledDate={(current) => {
                return current && current < validStartDate;
              }}
              {...bindDateInputProps({
                name: "contract.issuanceDate",
                values,
                setFieldValue,
                ...formProps,
              })}
              onChange={(date) => {
                setFieldValue("contract.issuanceDate", date);
                if (
                  date.isValid() &&
                  date >= validStartDate &&
                  date <= validEndDate
                ) {
                  if (recalculateCost) {
                    recalculateCost({
                      oldIssuanceDate: oldIssuanceDate,
                      issuanceDate: date,
                      setFieldValue,
                      setShowChangeCostModal,
                    });
                  }
                }
              }}
              onBlur={({ target: { value: issuanceDate } }) => {
                const date = dayjs(issuanceDate, DATE_FORMAT).add(2, "h");
                if (
                  date.isValid() &&
                  date >= validStartDate &&
                  date <= validEndDate
                ) {
                  setFieldValue("contract.issuanceDate", date);
                } else formProps.setFieldTouched("contract.issuanceDate");
              }}
              // disabled={locked}
              allowClear={false}
            />
          </div>

          {/* Checkbox panels */}
          <div className="miltis-form__checkboxes">
            {/* Existing contract checkbox */}
            <CheckboxPanel
              checked={values?.fields?.hasExistingContract || false}
              onChange={handleExistingContractChange}
              expandContent={renderExistingContractContent()}
            >
              <span
                dangerouslySetInnerHTML={{
                  __html:
                    translate?.EXISTING_CONTRACT?.LABEL ||
                    "Je dispose d'un contrat santé auprès d'un <strong>autre organisme assureur</strong>.",
                }}
              />
            </CheckboxPanel>

            {/* PPE checkbox */}
            <CheckboxPanel
              checked={values?.fields?.isPPE || false}
              onChange={handlePPEChange}
              expandContent={renderPPEContent()}
            >
              <span
                dangerouslySetInnerHTML={{
                  __html:
                    translate?.PPE?.LABEL ||
                    "Je suis une <strong>Personne Politiquement Exposée</strong> (PPE*) comme définie ci-dessous.",
                }}
              />
            </CheckboxPanel>

            {/* PPE footnote */}
            <p className="miltis-form__footnote">
              {translate?.PPE?.FOOTNOTE ||
                "* Une Personne Politiquement Exposée (PPE) est une personne qui exerce ou a cessé d’exercer depuis moins d’un an, une fonction publique importante en France, dans un pays étranger ou au sein d’une organisation internationale (ex. chef d'État, ministre, parlementaire, magistrat, dirigeant d'entreprise publique…), ainsi que certains membres de sa famille (conjoint, enfants, parents) ou les personnes connues pour lui être étroitement associées."}
            </p>
          </div>

          <ActionButtons
            disabled={!isValid}
            onNext={handleSubmit}
            onBack={onBack}
          />

          <DataProtection />
        </div>
      </div>
    </Layout>
  );
}

export default View;
